import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {DocumentHal} from '../../models/HAL/document-hal.model';
import {TranslatePipe} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {PostMessageSubscribeTypeEnum} from '../../enums/post-message-subscribe-type.enum';
import {PostMessageSubscriberService} from '../../services/post-message-subscriber.service';
import {PostMessageService} from '../../services/post-message.service';
import {LoadingSpinnerService} from '../../services/loading-spinner.service';
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {Team} from '../../models/HAL/team-hal.model';
import {SessionService} from '../../services/session.service';
import {NgClass} from '@angular/common';
import {SnackBarService} from '../../services/snackbar.service';
import {DialogService} from '../../services/dialog.service';
import {DynamicConfirmationDialogComponent} from '../dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';
import {MatDialogRef} from '@angular/material/dialog';
import {FormActionsHelperService} from '../../services/form-actions-helper.service';
import {MatMenuModule} from '@angular/material/menu';
import {OverflowButton} from '../../models/overflow-button.model';

@Component({
  selector: 'app-preview-form-metadata',
  imports: [
    TranslatePipe,
    MatButton,
    MatIconButton,
    MatIcon,
    NgClass,
    MatMenuModule
  ],
  templateUrl: './preview-form-metadata.component.html',
  styleUrl: './preview-form-metadata.component.scss'
})
export class PreviewFormMetadataComponent implements OnDestroy, AfterViewInit {

  @Input()
  public documentHal: DocumentHal | undefined

  protected hiddenButtons = new Set<string>();
  protected hiddenMenuButtons: OverflowButton[] = [];
  protected overflowButtons: OverflowButton[] = [];

  @ViewChild('breadCrumbsAndButtonsContainer') private readonly containerRef!: ElementRef<HTMLElement>;
  @ViewChild('breadcrumbEl') private readonly breadcrumbRef!: ElementRef<HTMLElement>;
  @ViewChild('primaryButton', {read: ElementRef}) private readonly primaryButtonRef!: ElementRef<HTMLElement>;
  @ViewChildren('measureButton', {read: ElementRef}) private readonly measureButtons!: QueryList<ElementRef<HTMLElement>>;

  private resizeObserver?: ResizeObserver;

  constructor(private readonly postMessageSubscriberService: PostMessageSubscriberService,
              private readonly postMessageService: PostMessageService,
              private readonly loadingSpinnerService: LoadingSpinnerService,
              private readonly router: Router,
              private readonly sessionService: SessionService,
              private readonly snackBarService: SnackBarService,
              private readonly dialogService: DialogService,
              private readonly loadingSpinner: LoadingSpinnerService,
              public readonly formActionsHelperService: FormActionsHelperService,
              private readonly ngZone: NgZone) {
    this.subscribeToDataCapturing();
    this.subscribeToSaveProgress();
    this.subscribeToPreviewRendering();
    this.initOverflowButtons();
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.postMessageSubscriberService.unsubscribe(PostMessageSubscribeTypeEnum.DATA_CAPTURING);
    this.postMessageSubscriberService.unsubscribe(PostMessageSubscribeTypeEnum.SAVE_PROGRESS);
    this.postMessageSubscriberService.unsubscribe(PostMessageSubscribeTypeEnum.RENDER_PREVIEW);
  }

  private initOverflowButtons(): void {
    this.overflowButtons = [
      {
        key: 'renderPreview',
        icon: 'preview',
        labelKey: 'previewFormMetadata.renderPreview',
        buttonType: 'mat-button',
        action: () => this.renderPreview(),
        visible: () => true,
        disabled: () => !this.documentHal?.renderPreviewEnabled
      },
      {
        key: 'save',
        icon: 'save',
        labelKey: 'previewFormMetadata.save',
        buttonType: 'mat-button',
        action: () => this.formActionsHelperService.saveFormProgress(this.documentHal!),
        visible: () => true,
        disabled: () => !this.documentHal?.saveProgressEnabled
      }
    ];
  }

  private setupResizeObserver(): void {
    const container = this.containerRef?.nativeElement;
    if (!container) return;
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.recalculateOverflow());
      this.resizeObserver.observe(container);
    });
    this.measureButtons.changes.subscribe(() => this.recalculateOverflow());
    setTimeout(() => this.recalculateOverflow());
  }

  private recalculateOverflow(): void {
    const container = this.containerRef?.nativeElement;
    const breadcrumb = this.breadcrumbRef?.nativeElement;
    const primaryBtn = this.primaryButtonRef?.nativeElement;
    if (!container || !breadcrumb) return;

    const containerWidth = container.clientWidth;
    const primaryBtnWidth = primaryBtn ? primaryBtn.offsetWidth + 8 : 0;
    const overflowIconWidth = 48;
    const gap = 8;

    const breadcrumbWidth = breadcrumb.offsetWidth;

    const buttonWidths = this.getButtonMeasurements();
    const visibleButtons = this.overflowButtons.filter(b => b.visible());
    const newHidden = new Set<string>();

    let usedWidth = breadcrumbWidth + primaryBtnWidth + gap;
    for (const btn of visibleButtons) {
      usedWidth += (buttonWidths.get(btn.key) ?? 0) + gap;
    }

    if (usedWidth > containerWidth) {
      usedWidth += overflowIconWidth;
      for (const btn of visibleButtons) {
        if (usedWidth <= containerWidth) break;
        const btnWidth = (buttonWidths.get(btn.key) ?? 0) + gap;
        newHidden.add(btn.key);
        usedWidth -= btnWidth;
      }
    }

    if (this.setsEqual(this.hiddenButtons, newHidden)) return;

    this.ngZone.run(() => {
      this.hiddenButtons = newHidden;
      this.hiddenMenuButtons = this.overflowButtons.filter(b => b.visible() && newHidden.has(b.key));
    });
  }

  private setsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  }

  private getButtonMeasurements(): Map<string, number> {
    const map = new Map<string, number>();
    if (!this.measureButtons) return map;
    this.measureButtons.forEach(ref => {
      const key = ref.nativeElement.dataset['key'];
      if (key) map.set(key, ref.nativeElement.offsetWidth);
    });
    return map;
  }

  protected isButtonHidden(key: string): boolean {
    return this.hiddenButtons.has(key);
  }

  protected backToCaseDetail() {
    this.loadingSpinner.show();
    this.router.navigate([new DocumentHal(this.documentHal)._links.getLink('case')?.getRoute()]).then();
  }

  protected backToCaseDetailWithDialog() {
    this.openUnsavedChangesDialog().afterClosed().subscribe((result) => {
      if (result) {
        this.loadingSpinner.show();
        this.router.navigate([new DocumentHal(this.documentHal)._links.getLink('case')?.getRoute()]).then();
      }
    });
  }

  protected backToCaseOverviewWithDialog() {
    this.openUnsavedChangesDialog().afterClosed().subscribe((result) => {
      if (result) {
        this.loadingSpinner.show();
        this.router.navigate([new Team(this.sessionService.getSession().currentTeam)._links.getLink('caseOverview')?.getRoute()]).then();
      }
    });
  }

  protected openUnsavedChangesDialog(): MatDialogRef<any, any> {
    return this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: "previewForm.unsavedChangesDialog.title",
        message: "previewForm.unsavedChangesDialog.message",
        confirmButtonText: "previewForm.unsavedChangesDialog.confirmButtonText",
        cancelButtonText: "previewForm.unsavedChangesDialog.cancelButtonText"
      }
    });
  }


  private subscribeToDataCapturing() {
    this.postMessageSubscriberService.subscribe(PostMessageSubscribeTypeEnum.DATA_CAPTURING, (data) => {
      if (data?.state === "COMPLETE") {
        this.loadingSpinnerService.hide();
        this.backToCaseDetail();
        this.snackBarService.open("previewForm.dataCapturingCompleted", 3);
      }
      if (data?.state === "NOT_COMPLETE") {
        this.loadingSpinnerService.hide();
        this.snackBarService.open("previewForm.dataCapturingNotCompleted", 3, "error");
      }
    });
  }

  private subscribeToPreviewRendering() {
    this.postMessageSubscriberService.subscribe(PostMessageSubscribeTypeEnum.RENDER_PREVIEW, (data) => {
      if (data?.state === "COMPLETE") {
        const fileURL = window.URL.createObjectURL(this.b64toBlob(data.message, "application/pdf"));
        window.open(fileURL, "_blank");
        this.loadingSpinnerService.hide();
        this.snackBarService.open("previewForm.previewRenderingCompleted", 3);
      }
      if (data?.state === "NOT_COMPLETE") {
        this.loadingSpinnerService.hide();
        this.snackBarService.open("previewForm.previewRenderingNotCompleted", 3, "error");
      }
    });
  }

  private b64toBlob(b64Data: string, contentType: string) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, {type: contentType});
  }

  private subscribeToSaveProgress() {
    this.postMessageSubscriberService.subscribe(PostMessageSubscribeTypeEnum.SAVE_PROGRESS, (data) => {
      if (data?.state === "COMPLETE") {
        this.loadingSpinnerService.hide();
        this.backToCaseDetail();
        this.snackBarService.open("previewForm.saveProgressCompleted", 3);
      } else {
        this.loadingSpinnerService.hide();
        this.snackBarService.open("previewForm.saveProgressNotCompleted", 3, "error");
      }
    });
  }

  public renderPreview() {
    this.loadingSpinnerService.show();
    if (this.documentHal) {
      const postMessage = this.postMessageService.getRenderPreviewPostMessageModel(this.documentHal);
      const formPreviewElement = document.querySelector("#formPreview") as HTMLIFrameElement;
      if (formPreviewElement) {
        this.postMessageService.postMessage(formPreviewElement, postMessage);
      } else {
        this.loadingSpinnerService.hide();
      }
    } else {
      this.loadingSpinnerService.hide();
    }
  }
}

